import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, Clock, DollarSign, X } from 'lucide-react';

const Recipes = ({ user }) => {
  const [recipes, setRecipes] = useState([]);
  const [ingredients, setIngredients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingRecipe, setEditingRecipe] = useState(null);
  const [formData, setFormData] = useState({
    nome: '',
    rendimento: '',
    unidade_rendimento: '',
    tempo_preparo: '',
    custo_mao_obra_hora: '50',
    modo_preparo: '',
    ingredientes: [],
  });
  const [selectedIngredient, setSelectedIngredient] = useState('');
  const [ingredientQuantity, setIngredientQuantity] = useState('');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [recipesRes, ingredientsRes] = await Promise.all([
        axios.get(`${API}/recipes`),
        axios.get(`${API}/ingredients`),
      ]);
      setRecipes(recipesRes.data || []);
      setIngredients(ingredientsRes.data || []);
    } catch (error) {
      toast.error('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  const addIngredient = () => {
    if (!selectedIngredient || !ingredientQuantity) {
      toast.error('Selecione um ingrediente e quantidade');
      return;
    }

    const ingredient = ingredients.find((i) => i.id === selectedIngredient);
    if (!ingredient) return;

    setFormData({
      ...formData,
      ingredientes: [
        ...formData.ingredientes,
        {
          ingredient_id: ingredient.id,
          ingredient_name: ingredient.nome,
          quantidade: parseFloat(ingredientQuantity),
          unidade: ingredient.unidade_embalagem || '',
        },
      ],
    });
    setSelectedIngredient('');
    setIngredientQuantity('');
  };

  const removeIngredient = (index) => {
    setFormData({
      ...formData,
      ingredientes: formData.ingredientes.filter((_, i) => i !== index),
    });
  };

  const calculateCosts = () => {
    let custoIngredientes = 0;
    formData.ingredientes.forEach((ing) => {
      const ingredient = ingredients.find((i) => i.id === ing.ingredient_id);
      if (ingredient && !isNaN(ingredient.preco_unitario_real)) {
        custoIngredientes += ingredient.preco_unitario_real * ing.quantidade;
      }
    });
    const custoMaoObra =
      parseFloat(formData.tempo_preparo || 0) * parseFloat(formData.custo_mao_obra_hora || 0);
    const custoTotal = custoIngredientes + custoMaoObra;
    const custoUnitario =
      parseFloat(formData.rendimento) > 0 ? custoTotal / parseFloat(formData.rendimento) : 0;
    return { custoTotal, custoUnitario };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (formData.ingredientes.length === 0) {
      toast.error('Adicione pelo menos um ingrediente');
      return;
    }

    const data = {
      ...formData,
      rendimento: parseFloat(formData.rendimento),
      tempo_preparo: parseFloat(formData.tempo_preparo),
      custo_mao_obra_hora: parseFloat(formData.custo_mao_obra_hora),
    };

    try {
      if (editingRecipe) {
        await axios.put(`${API}/recipes/${editingRecipe.id}`, data);
        toast.success('Receita atualizada!');
      } else {
        await axios.post(`${API}/recipes`, data);
        toast.success('Receita criada!');
      }
      setDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      // Transformar qualquer objeto em string antes de mostrar
      const msg =
        error.response?.data?.detail
          ? typeof error.response.data.detail === 'string'
            ? error.response.data.detail
            : JSON.stringify(error.response.data.detail)
          : 'Erro ao salvar receita';
      toast.error(msg);
    }
  };

  const handleEdit = (recipe) => {
    setEditingRecipe(recipe);
    setFormData({
      nome: recipe.nome || '',
      rendimento: recipe.rendimento?.toString() || '',
      unidade_rendimento: recipe.unidade_rendimento || '',
      tempo_preparo: recipe.tempo_preparo?.toString() || '',
      custo_mao_obra_hora: recipe.custo_mao_obra_hora?.toString() || '50',
      modo_preparo: recipe.modo_preparo || '',
      ingredientes: recipe.ingredientes || [],
    });
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Deseja realmente excluir esta receita?')) return;
    try {
      await axios.delete(`${API}/recipes/${id}`);
      toast.success('Receita excluída!');
      fetchData();
    } catch (error) {
      toast.error('Erro ao excluir receita');
    }
  };

  const resetForm = () => {
    setFormData({
      nome: '',
      rendimento: '',
      unidade_rendimento: '',
      tempo_preparo: '',
      custo_mao_obra_hora: '50',
      modo_preparo: '',
      ingredientes: [],
    });
    setEditingRecipe(null);
    setSelectedIngredient('');
    setIngredientQuantity('');
  };

  if (loading) {
    return <div className="text-center py-8">Carregando...</div>;
  }

  const isAdmin = user?.role === 'Admin';
  const { custoTotal, custoUnitario } = calculateCosts();

  return (
    <div className="space-y-6" data-testid="recipes-page">
      {/* Cabeçalho */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-4xl font-bold bg-gradient-to-r from-pink-600 to-rose-500 bg-clip-text text-transparent mb-2">
            Receitas
          </h1>
          <p className="text-gray-600">Gerencie suas receitas e custos</p>
        </div>

        {/* Botão para Admin */}
        {isAdmin && (
          <Dialog
            open={dialogOpen}
            onOpenChange={(open) => {
              setDialogOpen(open);
              if (!open) resetForm();
            }}
          >
            <DialogTrigger asChild>
              <Button className="bg-gradient-to-r from-pink-500 to-rose-400 hover:from-pink-600 hover:to-rose-500" data-testid="add-recipe-button">
                <Plus className="mr-2 h-4 w-4" /> Nova Receita
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="recipe-dialog">
              <DialogHeader>
                <DialogTitle>{editingRecipe ? 'Editar Receita' : 'Nova Receita'}</DialogTitle>
                <DialogDescription>Preencha os dados da receita</DialogDescription>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                {/* Nome */}
                <div className="space-y-2">
                  <Label htmlFor="nome">Nome</Label>
                  <Input
                    id="nome"
                    value={formData.nome}
                    onChange={(e) => setFormData({ ...formData, nome: e.target.value })}
                    required
                    data-testid="recipe-name-input"
                  />
                </div>

                {/* Rendimento e Unidade */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="rendimento">Rendimento</Label>
                    <Input
                      id="rendimento"
                      type="number"
                      step="0.01"
                      value={formData.rendimento}
                      onChange={(e) => setFormData({ ...formData, rendimento: e.target.value })}
                      required
                      data-testid="recipe-yield-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="unidade_rendimento">Unidade Rendimento</Label>
                    <Input
                      id="unidade_rendimento"
                      placeholder="unidades, kg, etc"
                      value={formData.unidade_rendimento}
                      onChange={(e) => setFormData({ ...formData, unidade_rendimento: e.target.value })}
                      required
                      data-testid="recipe-unit-input"
                    />
                  </div>
                </div>

                {/* Tempo e Custo */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="tempo_preparo">Tempo Preparo (horas)</Label>
                    <Input
                      id="tempo_preparo"
                      type="number"
                      step="0.01"
                      value={formData.tempo_preparo}
                      onChange={(e) => setFormData({ ...formData, tempo_preparo: e.target.value })}
                      required
                      data-testid="recipe-time-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="custo_mao_obra_hora">Custo Mão de Obra (R$/h)</Label>
                    <Input
                      id="custo_mao_obra_hora"
                      type="number"
                      step="0.01"
                      value={formData.custo_mao_obra_hora}
                      onChange={(e) => setFormData({ ...formData, custo_mao_obra_hora: e.target.value })}
                      required
                      data-testid="recipe-labor-cost-input"
                    />
                  </div>
                </div>

                {/* Custos */}
                <div className="grid grid-cols-2 gap-4 mt-2">
                  <div className="bg-green-50 p-3 rounded-lg">
                    <p className="text-xs text-gray-600">Custo Total</p>
                    <p className="font-semibold">R$ {custoTotal.toFixed(2)}</p>
                  </div>
                  <div className="bg-pink-50 p-3 rounded-lg">
                    <p className="text-xs text-gray-600">Custo Unitário</p>
                    <p className="font-semibold">R$ {custoUnitario.toFixed(2)}</p>
                  </div>
                </div>

                {/* Modo de Preparo */}
                <div className="space-y-2">
                  <Label htmlFor="modo_preparo">Modo de Preparo</Label>
                  <Textarea
                    id="modo_preparo"
                    value={formData.modo_preparo}
                    onChange={(e) => setFormData({ ...formData, modo_preparo: e.target.value })}
                    rows={4}
                    required
                    data-testid="recipe-instructions-input"
                  />
                </div>

                {/* Ingredientes */}
                <div className="space-y-2">
                  <Label>Ingredientes</Label>
                  <div className="flex gap-2">
                    <Select value={selectedIngredient} onValueChange={setSelectedIngredient}>
                      <SelectTrigger className="flex-1" data-testid="select-ingredient">
                        <SelectValue placeholder="Selecione ingrediente" />
                      </SelectTrigger>
                      <SelectContent>
                        {ingredients.map((ing) => (
                          <SelectItem key={ing.id} value={ing.id}>
                            {ing.nome} ({ing.unidade_embalagem})
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Input
                      type="number"
                      step="0.01"
                      placeholder="Qtd"
                      value={ingredientQuantity}
                      onChange={(e) => setIngredientQuantity(e.target.value)}
                      className="w-24"
                      data-testid="ingredient-quantity-input"
                    />
                    <Button type="button" onClick={addIngredient} size="sm" data-testid="add-ingredient-to-recipe">
                      <Plus className="h-4 w-4" />
                    </Button>
                  </div>

                  {formData.ingredientes.length > 0 && (
                    <div className="space-y-2 mt-3">
                      {formData.ingredientes.map((ing, index) => (
                        <div
                          key={index}
                          className="flex items-center justify-between bg-pink-50 p-2 rounded"
                          data-testid={`recipe-ingredient-${index}`}
                        >
                          <span className="text-sm">
                            {String(ing.ingredient_name)}: {String(ing.quantidade)} {String(ing.unidade)}
                          </span>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => removeIngredient(index)}
                            data-testid={`remove-ingredient-${index}`}
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <Button type="submit" className="w-full bg-gradient-to-r from-pink-500 to-rose-400" data-testid="recipe-submit-button">
                  {editingRecipe ? 'Atualizar' : 'Criar'}
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {/* Lista de receitas */}
      {recipes.length === 0 ? (
        <Card className="border-pink-100 bg-white/80 backdrop-blur-sm" data-testid="no-recipes">
          <CardContent className="py-12 text-center">
            <p className="text-gray-500">Nenhuma receita cadastrada ainda.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {recipes.map((recipe) => {
            // Garantir que todos os campos sejam strings ou números
            const nome = String(recipe.nome || '');
            const rendimento = String(recipe.rendimento || '');
            const unidade = String(recipe.unidade_rendimento || '');
            const tempo = String(recipe.tempo_preparo || '');
            const custoT = parseFloat(recipe.custo_total) || 0;
            const custoU = parseFloat(recipe.custo_unitario) || 0;
            const modo = String(recipe.modo_preparo || '');
            const recIngs = Array.isArray(recipe.ingredientes) ? recipe.ingredientes : [];

            return (
              <Card
                key={recipe.id}
                className="border-pink-100 bg-white/80 backdrop-blur-sm shadow-md hover:shadow-xl transition-shadow"
                data-testid={`recipe-card-${recipe.id}`}
              >
                <CardHeader>
                  <CardTitle className="text-2xl">{nome}</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-blue-50 p-3 rounded-lg">
                      <p className="text-xs text-gray-600 mb-1">Rendimento</p>
                      <p className="font-semibold">
                        {rendimento} {unidade}
                      </p>
                    </div>
                    <div className="bg-purple-50 p-3 rounded-lg">
                      <p className="text-xs text-gray-600 mb-1 flex items-center gap-1">
                        <Clock className="w-3 h-3" /> Tempo
                      </p>
                      <p className="font-semibold">{tempo}h</p>
                    </div>
                    <div className="bg-green-50 p-3 rounded-lg">
                      <p className="text-xs text-gray-600 mb-1 flex items-center gap-1">
                        <DollarSign className="w-3 h-3" /> Custo Total
                      </p>
                      <p className="font-semibold">R$ {custoT.toFixed(2)}</p>
                    </div>
                    <div className="bg-pink-50 p-3 rounded-lg">
                      <p className="text-xs text-gray-600 mb-1">Custo Unitário</p>
                      <p className="font-semibold">R$ {custoU.toFixed(2)}</p>
                    </div>
                  </div>

                  {/* Ingredientes */}
                  <div>
                    <p className="text-sm font-semibold text-gray-700 mb-2">Ingredientes:</p>
                    <ul className="space-y-1">
                      {recIngs.map((ing, idx) => (
                        <li key={idx} className="text-sm text-gray-600">
                          • {String(ing.ingredient_name)}: {String(ing.quantidade)} {String(ing.unidade)}
                        </li>
                      ))}
                    </ul>
                  </div>

                  {/* Modo de preparo */}
                  <div>
                    <p className="text-sm font-semibold text-gray-700 mb-2">Modo de Preparo:</p>
                    <p className="text-sm text-gray-600 whitespace-pre-line">{modo}</p>
                  </div>

                  {isAdmin && (
                    <div className="flex gap-2 pt-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleEdit(recipe)}
                        className="flex-1 border-pink-300 hover:bg-pink-50"
                        data-testid={`edit-recipe-${recipe.id}`}
                      >
                        <Pencil className="w-4 h-4 mr-1" /> Editar
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleDelete(recipe.id)}
                        className="flex-1 border-red-300 hover:bg-red-50 text-red-600"
                        data-testid={`delete-recipe-${recipe.id}`}
                      >
                        <Trash2 className="w-4 h-4 mr-1" /> Excluir
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default Recipes;
